import { z } from 'zod';

export const roomIdSchema = z.enum([
  'docking-throat',
  'maintenance-access',
  'crew-corridor',
  'power-control',
  'cargo-blister',
  'navigation-chamber',
  'observation-dome',
  'service-shaft',
]);

export const salvageIdSchema = z.enum([
  'navigation-core',
  'data-prism',
  'reactor-plate',
  'medical-case',
  'tool-cassette',
  'flight-recorder',
]);

export const busIdSchema = z.enum(['A', 'B']);
export const pressureSchema = z.enum(['pressurized', 'vacuum']);
export const optionalClassSchema = z.enum(['precision', 'heavy', 'field']);

export const roomSchema = z.object({
  id: roomIdSchema,
  name: z.string().min(1),
  index: z.number().int().min(1).max(8),
  pressure: pressureSchema,
  bus: busIdSchema.nullable(),
  position: z.object({ x: z.number(), y: z.number() }),
  tutorial: z.boolean(),
});

export const connectionSchema = z.object({
  id: z.string().min(1),
  from: roomIdSchema,
  to: roomIdSchema,
  kind: z.enum(['open', 'door', 'cut']),
  bus: busIdSchema.nullable(),
  initiallyOpen: z.boolean(),
  normal: z.object({ x: z.number().min(-1).max(1), y: z.number().min(-1).max(1) }),
  structuralEvent: z.enum(['narrow-at-35', 'collapse-at-65']).nullable(),
});

export const salvageSchema = z.object({
  id: salvageIdSchema,
  name: z.string().min(1),
  roomId: roomIdSchema,
  mass: z.number().positive(),
  baseValue: z.number().int().positive(),
  fragility: z.number().min(0).max(1),
  required: z.boolean(),
  optionalClass: optionalClassSchema.nullable(),
  attachmentIds: z.array(z.string()),
  hidden: z.boolean(),
});

export const cutPointSchema = z.object({
  id: z.string().min(1),
  roomId: roomIdSchema,
  target: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('connection'), connectionId: z.string().min(1) }),
    z.object({ kind: z.literal('salvage'), salvageId: salvageIdSchema }),
  ]),
  durationSeconds: z.number().positive(),
  heatRate: z.number().positive(),
  coolRate: z.number().positive(),
  heatLimit: z.number().positive(),
  fragility: z.number().min(0).max(1),
});

export const wreckContentSchema = z.object({
  id: z.literal('last-wreck'),
  contractTitle: z.string().min(1),
  requiredSalvageId: salvageIdSchema,
  extractionRoomId: roomIdSchema,
  rooms: z.array(roomSchema).length(8),
  connections: z.array(connectionSchema).min(8),
  salvage: z.array(salvageSchema).length(6),
  cutPoints: z.array(cutPointSchema).min(4),
  tuning: z.object({
    scanCooldownSeconds: z.number().positive(),
    scanRevealSeconds: z.number().positive(),
    grappleRange: z.number().positive(),
    grappleWarningTension: z.number().positive(),
    cableBreakMultiplier: z.number().gt(1),
    baseInstabilityPerSecond: z.number().positive(),
    emergencySeconds: z.number().positive(),
  }),
});

export type RoomId = z.infer<typeof roomIdSchema>;
export type SalvageId = z.infer<typeof salvageIdSchema>;
export type BusId = z.infer<typeof busIdSchema>;
export type OptionalClass = z.infer<typeof optionalClassSchema>;
export type WreckContent = z.infer<typeof wreckContentSchema>;

export function validateContentReferences(content: WreckContent): string[] {
  const issues: string[] = [];
  const roomIds = new Set(content.rooms.map((room) => room.id));
  const connectionIds = new Set(content.connections.map((connection) => connection.id));
  const salvageById = new Map(content.salvage.map((salvage) => [salvage.id, salvage]));
  const cutPointIds = new Set(content.cutPoints.map((point) => point.id));

  if (roomIds.size !== 8) issues.push('Room ids must be unique.');
  if (new Set(content.rooms.map((room) => room.index)).size !== 8) {
    issues.push('Room indices must be unique.');
  }
  if (salvageById.size !== 6) issues.push('Salvage ids must be unique.');
  if (connectionIds.size !== content.connections.length) {
    issues.push('Connection ids must be unique.');
  }
  if (cutPointIds.size !== content.cutPoints.length) {
    issues.push('Cut point ids must be unique.');
  }

  for (const connection of content.connections) {
    if (!roomIds.has(connection.from) || !roomIds.has(connection.to)) {
      issues.push(`Connection ${connection.id} references a missing room.`);
    }
    if (connection.from === connection.to) {
      issues.push(`Connection ${connection.id} cannot connect a room to itself.`);
    }
  }

  for (const point of content.cutPoints) {
    if (point.target.kind === 'connection') {
      const connectionId = point.target.connectionId;
      const connection = content.connections.find((candidate) => candidate.id === connectionId);
      if (connection?.kind !== 'cut') {
        issues.push(`Cut point ${point.id} must reference an authored cut connection.`);
      }
    } else {
      const salvage = salvageById.get(point.target.salvageId);
      if (!salvage?.attachmentIds.includes(point.id)) {
        issues.push(`Cut point ${point.id} is not listed on ${point.target.salvageId}.`);
      }
    }
  }

  for (const salvage of content.salvage) {
    for (const attachmentId of salvage.attachmentIds) {
      if (!cutPointIds.has(attachmentId)) {
        issues.push(`Salvage ${salvage.id} references missing attachment ${attachmentId}.`);
      }
    }
  }

  if (content.salvage.filter((salvage) => salvage.required).length !== 1) {
    issues.push('Exactly one salvage object must be required.');
  }
  if (!salvageById.get(content.requiredSalvageId)?.required) {
    issues.push('requiredSalvageId must reference the required salvage object.');
  }

  const reachable = new Set<RoomId>([content.extractionRoomId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const connection of content.connections) {
      if (reachable.has(connection.from) && !reachable.has(connection.to)) {
        reachable.add(connection.to);
        changed = true;
      } else if (reachable.has(connection.to) && !reachable.has(connection.from)) {
        reachable.add(connection.from);
        changed = true;
      }
    }
  }
  if (reachable.size !== 8) issues.push('All eight rooms must be topologically reachable.');

  return issues;
}

export function parseWreckContent(input: unknown): WreckContent {
  const content = wreckContentSchema.parse(input);
  const issues = validateContentReferences(content);
  if (issues.length > 0) throw new Error(`Invalid wreck content:\n${issues.join('\n')}`);
  return content;
}
